/**
 *  @file       widgets/accounts-list/account/account.jsx
 *  @author     Alex Huctwith <alex@kingsds.network>
 *
 *  @date       April 2022
 *
 *              A component for displaying an account's listing as part of the accounts list
 *              References an element with an id of the account's address which is the more
 *              pane for that component
 *              Props:
 *                - account: the keystore object for the account
 *                  - account.balance: the keystore object should have either a balance value or something falsey if locked
 */

import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyToClipboard } from '@/components/Elements';
import { AccountDropdown } from './AccountDropdown';
import { AccountsDispatchContext } from '../../../stores/AccountsContext';
import { AccountOperations } from '../../../api/AccountOperations';
import { Balance } from '../../Balance';

import lockedIcon from './locked.png';

import './Account.css';

/**
 * Renders a component for displaying an account's listing as part of the accounts list.
 *
 * @param   {object}      props         Component properties.
 * @param   {object}      props.account The account to display information for.
 * @returns {JSX.Element}               The table row component.
 */
export function Account({ account })
{
  const { t } = useTranslation();
  const { handleUpdateAccounts } = useContext(AccountsDispatchContext);
  const [isSelected, setIsSelected] = useState(false);

  function toggleIsSelected()
  {
    setIsSelected((prevState) => {
      return !prevState;
    });
  }

  async function unlockPressed()
  {
    await account.unlock(null, 200, true);
    const updatedAccount = await AccountOperations.refreshBalance([account]);
    if (updatedAccount)
      handleUpdateAccounts(updatedAccount);
  };

  return (
    <tr
      id={account.address}
      className={`accountEntry${account.isLocked() ? ' locked' : ''}${
        isSelected ? ' accountSelected' : ''
      }`}
    >
      <td>{account.label}</td>
      <td className="accountNumber">
        <div>
          <div className="text">{String(account.address).toLowerCase()}</div>
          <div className="icon">
            <CopyToClipboard copyText={String(account.address).toLowerCase()} className="xs-hidden"/>
          </div>
        </div>
      </td>
      <td className="xs-hidden">{account.isUnlocked() ? t('unlocked') : t('locked')}</td>
      <td className="balance">
        <Balance balance={account.balance} />
      </td>
      <td className="action">
        {account.isUnlocked() ? (
          <AccountDropdown
            className="dropdownMenu"
            account={account}
            onOpenChanged={toggleIsSelected}
          />
        ) : (
          <button
            id={`unlockAccount${account.address}`}
            onClick={unlockPressed}
          >
            <img src={lockedIcon} alt="Locked Account" />
          </button>
        )}
      </td>
    </tr>
  );
}
